import api from "../../../../services/api";

import type {
  WebhookIntegrationWithSecret,
  WebhookEventsPage,
  WebhookIntegration,
  WebhookIntegrationForm,
} from "../../../../feature-module/crm/webhook-integrations/types";

interface CompanyScope {
  companyId: string;
  companyType: string;
}

const scopePayload = ({ companyId, companyType }: CompanyScope) => ({
  signature: localStorage.getItem("signature"),
  company_id: parseInt(companyId),
  company_type: companyType,
});

const unwrap = <T>(response: any): T => response.data.message[0].data;

const cleanForm = (form: Partial<WebhookIntegrationForm>) =>
  Object.fromEntries(
    Object.entries(form).filter(([, value]) => value !== undefined),
  );

export const fetchWebhookIntegrations = async (
  scope: CompanyScope,
): Promise<WebhookIntegration[]> =>
  unwrap(await api.post("leadService/webhookIntegration/list", scopePayload(scope)));

export const createWebhookIntegration = async (
  scope: CompanyScope,
  form: WebhookIntegrationForm,
): Promise<WebhookIntegrationWithSecret> =>
  unwrap(
    await api.post("leadService/webhookIntegration/create", {
      ...scopePayload(scope),
      ...cleanForm(form),
    }),
  );

export const updateWebhookIntegration = async (
  scope: CompanyScope,
  id: number,
  form: Partial<Omit<WebhookIntegrationForm, "channel" | "provider">>,
): Promise<WebhookIntegration> =>
  unwrap(
    await api.post("leadService/webhookIntegration/update", {
      ...scopePayload(scope),
      id,
      ...cleanForm(form),
    }),
  );

export const toggleWebhookIntegrationStatus = async (
  scope: CompanyScope,
  id: number,
  status: number,
): Promise<WebhookIntegration> =>
  unwrap(
    await api.post("leadService/webhookIntegration/toggleStatus", {
      ...scopePayload(scope),
      id,
      status,
    }),
  );

export const regenerateWebhookKey = async (
  scope: CompanyScope,
  id: number,
): Promise<WebhookIntegrationWithSecret> =>
  unwrap(
    await api.post("leadService/webhookIntegration/regenerateKey", {
      ...scopePayload(scope),
      id,
    }),
  );

export const deleteWebhookIntegration = async (
  scope: CompanyScope,
  id: number,
): Promise<void> => {
  await api.post("leadService/webhookIntegration/delete", {
    ...scopePayload(scope),
    id,
  });
};

export const fetchWebhookIntegrationEvents = async (
  scope: CompanyScope,
  id: number,
  page: number,
  limit: number,
): Promise<WebhookEventsPage> =>
  unwrap(
    await api.post("leadService/webhookIntegration/events", {
      ...scopePayload(scope),
      id,
      page,
      limit,
    }),
  );
